/**
 * Chart palette + semantic-color helpers. Kept in a plain `.ts` module (no
 * Vue SFC imports) so it can be unit-tested without compiling any `.vue`
 * component. Re-exported from the chart barrel.
 */

/**
 * Shared chart palette tokens. Every DCS app defines `--chart-1..5` for its
 * light and dark themes, so series visuals, legend bullets, tooltip swatches,
 * and crosshair dots all resolve to the same colors.
 *
 * Keep these as bare `var(--chart-N)` — apps define the tokens as full color
 * values (oklch/rgb), so wrapping them in `hsl(...)` produces invalid CSS.
 */
const chartColorTokens = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
]

/**
 * Returns the default chart color palette: solid `var(--chart-N)` tokens,
 * cycling when more than five series are requested.
 *
 * The zero-arg default is the FULL five-token palette, matching
 * {@link chartPalette} (C-207 item 6 — the two helpers used to default to 3
 * vs 5). Every in-repo chart call site passes an explicit count
 * (`categories.length`), so unifying on 5 changes no rendered chart; 5 wins
 * because the zero-arg `chartPalette()` form is what call sites actually use.
 *
 * Historical note: this helper used to return
 * `hsl(var(--vis-primary-color) / α)` ramps — a different scheme from the
 * legend/tooltip colors that rendered as invalid CSS in apps with oklch
 * themes. All chart types now share the `--chart-N` palette.
 */
export function defaultColors(count: number = chartColorTokens.length): string[] {
  return Array.from({ length: count }, (_, i) => chartColorTokens[i % chartColorTokens.length])
}

/**
 * Backward-compatible alias of {@link defaultColors}. Charts that need
 * translucency apply opacity at the SVG layer (gradients / fill-opacity),
 * never by baking it into the palette.
 */
export const solidColors = defaultColors

/**
 * The canonical DCS chart palette as an explicit array. Use at call sites that
 * pass `:colors` to a chart so every dashboard shares one palette source of
 * truth instead of copy-pasting `['var(--chart-1)', …]` (phase-2 divergence
 * #6). Returns the same tokens, in the same order, as {@link defaultColors} —
 * so swapping a local array for this is a zero-visual-change refactor.
 *
 * @param count Number of colors to return (cycles past five). Defaults to the
 *   full five-token palette.
 */
export function chartPalette(count: number = chartColorTokens.length): string[] {
  return defaultColors(count)
}

/**
 * Semantic chart roles → palette tokens.
 *
 * Grounds the "arbitrary per-view palette-index picks" cleanup (phase-2
 * divergence #7) in the palette's own role assignments (see the chart-system
 * -unification record): chart-1 steel-teal (primary), chart-2 terracotta
 * (secondary), chart-3 green, chart-4 indigo, chart-5 amber — plus the theme's
 * `--destructive` for danger and `--muted-foreground` for neutral. Use these
 * instead of `chartColors[n]` wherever a series carries meaning (status /
 * health / good-vs-bad) so, e.g., a "Cancelled" slice reads red, not green.
 */
export const semanticChartColors = {
  primary: 'var(--chart-1)',
  secondary: 'var(--chart-2)',
  success: 'var(--chart-3)',
  info: 'var(--chart-4)',
  warning: 'var(--chart-5)',
  danger: 'var(--destructive)',
  neutral: 'var(--muted-foreground)',
} as const

export type SemanticChartRole = keyof typeof semanticChartColors

/** Resolve one semantic role to its palette token. */
export function semanticChartColor(role: SemanticChartRole): string {
  return semanticChartColors[role]
}

/**
 * Resolve an ordered list of semantic roles to palette tokens — pass straight
 * to a chart's `:colors` when each series maps to a fixed meaning, e.g.
 * `semanticChartPalette(['success', 'warning', 'danger'])`.
 */
export function semanticChartPalette(roles: SemanticChartRole[]): string[] {
  return roles.map(semanticChartColor)
}
